import {
  HTMLAttributes,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useVisibleGroups } from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import SearchMultiSelect from "../ProcessTilesControls/SearchMultiSelect";
import { StyledOption } from "../ProcessTilesControls/ProcessTilesFilterControlsStyles";
import {
  Button,
  Checkbox,
  Chip,
  ClickAwayListener,
  IconButton,
  Paper,
  TextField,
} from "@mui/material";
import { Close, Search } from "@mui/icons-material";
import { lightTextBody } from "constants/colors";
import { useGroupTilesFilterState } from "components/Dashboard/Tiles/GroupTile/groupTilesUtils";
import styled from "styled-components/macro";
import { omit } from "lodash";
import { useWorkScheduleConfigJsonListAtom } from "store/atoms";
const SELECT_ALL = "SELECT_ALL";

/** Autocomplete dropdown search for filtering multiple groups by workSchedule */
export function GroupTilesFilterControls() {
  const visibleGroups = useVisibleGroups();
  const [workSchedules] = useWorkScheduleConfigJsonListAtom();
  const visibleGroupNames = useMemo(
    () =>
      visibleGroups
        .filter(g => g.processes.length > 0)
        .map(p => p.longName)
        .sort((a, b) => {
          const associatedWorkScheduleA =
            visibleGroups.find(group => group.longName === a)?.processes[0]
              ?.workScheduleConfig?.id ?? "";
          const associatedWorkScheduleB =
            visibleGroups.find(group => group.longName === b)?.processes[0]
              ?.workScheduleConfig?.id ?? "";
          return (
            Number(associatedWorkScheduleA) - Number(associatedWorkScheduleB)
          );
        }),
    [visibleGroups]
  );
  const { translate } = useFlightTranslation();
  const [
    { searchText, searchTextDisplay, selectedGroupNames },
    setGroupTilesFilterState,
  ] = useGroupTilesFilterState();
  const [open, setOpen] = useState<"closed" | "preview" | "open">("closed");

  return (
    <GroupTilesFilterControlsStyles
      className={`searchInput${searchText ? " active" : ""}`}
      onMouseEnter={() => {
        if (open === "closed") {
          setOpen("preview");
        }
      }}
      onClick={() => {
        if (open !== "open") {
          setOpen("open");
        }
      }}
      onMouseLeave={() => {
        if (open === "preview") {
          setOpen("closed");
        }
      }}
      onBlur={() => {
        if (open !== "closed") {
          setOpen("closed");
        }
      }}
      onKeyDown={e => {
        if (e.key === "Delete") {
          // delete the leftmost character (since cursor ends up on the left of searchText)
          setGroupTilesFilterState(prev => ({
            ...prev,
            searchTextDisplay: prev.searchTextDisplay.slice(1),
            searchText: prev.searchTextDisplay.slice(1),
          }));
        }
      }}
    >
      <SearchMultiSelect
        placeholderStr={translate("Search for a group")}
        selectedItems={selectedGroupNames}
        setSelectedItems={selectedItems => {
          // if there are too many groups and the page becomes slow when rendering/filtering, we can use startTransition commented-out lines below to ameliorate
          // startProcessTilesPanelPending();
          // startTransition(() => {
          const areAllCurrentlySelected =
            selectedGroupNames.length === visibleGroupNames.length;
          const shouldSelectAll =
            !areAllCurrentlySelected && selectedItems.includes(SELECT_ALL);
          const shouldSelectNone =
            areAllCurrentlySelected && selectedItems.includes(SELECT_ALL);
          setGroupTilesFilterState(p => ({
            ...p,
            searchText: null,
            searchTextDisplay: "",
            selectedGroupNames: shouldSelectAll
              ? visibleGroupNames
              : shouldSelectNone
                ? []
                : selectedItems.filter(item => item !== SELECT_ALL),
          }));
        }}
        title=""
        items={[SELECT_ALL, ...visibleGroupNames]}
        textValue={""} // handled by SelectedWorkschedulesChips
        AutocompleteProps={{
          disableCloseOnSelect: true,
          multiple: true,
          value: visibleGroupNames,
          open: ["open", "preview"].includes(open),
          PaperComponent: ({ children }) => (
            <ClickAwayListener
              onClickAway={() => {
                setOpen("closed");
              }}
            >
              <Paper
                style={{
                  position: "absolute",
                  zIndex: 1000,
                  width: "100%",
                  left: 0,
                  right: 0,
                  opacity: open === "preview" ? 0.8 : 1,
                }}
              >
                {children}
              </Paper>
            </ClickAwayListener>
          ),
          ListboxProps: {
            style: {
              maxHeight: "calc(100vh - 360px)",
              overflowY: "auto",
            },
          },
          ListboxComponent: CustomListbox,
          renderGroup: params => {
            const isSelectAll = params.group === "Select All";
            const areAllOptionsSelected =
              selectedGroupNames.length === visibleGroupNames.length ||
              selectedGroupNames.includes(SELECT_ALL);
            return (
              <div key={params.group}>
                <Button
                  style={{
                    textTransform: "none",
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-start",
                    height: 42,
                  }}
                  onClick={() => {
                    if (isSelectAll) {
                      setGroupTilesFilterState(p => ({
                        ...p,
                        selectedGroupNames: areAllOptionsSelected
                          ? []
                          : visibleGroupNames,
                      }));
                      return;
                    }
                    const allGroupMembers = params.children.map(
                      child => child.key
                    );
                    const areAllGroupMembersSelected = allGroupMembers.every(
                      member => selectedGroupNames.includes(member)
                    );
                    setGroupTilesFilterState(prev => ({
                      ...prev,
                      selectedGroupNames: areAllGroupMembersSelected
                        ? prev.selectedGroupNames.filter(
                            name => !allGroupMembers.includes(name)
                          )
                        : Array.from(
                            new Set([
                              ...prev.selectedGroupNames,
                              ...allGroupMembers,
                            ])
                          ),
                    }));
                  }}
                >
                  <Checkbox
                    checked={
                      isSelectAll
                        ? areAllOptionsSelected
                        : params.children.every(child =>
                            selectedGroupNames.includes(child.key)
                          )
                    }
                    indeterminate={
                      params.children.some(child =>
                        selectedGroupNames.includes(child.key)
                      ) &&
                      !params.children.every(child =>
                        selectedGroupNames.includes(child.key)
                      )
                    }
                  />
                  {params.group}
                </Button>
                {isSelectAll ? null : <div>{params.children}</div>}
              </div>
            );
          },
          renderOption: (
            props: HTMLAttributes<HTMLLIElement>,
            option: string
          ) => {
            const isSelectAll = option === SELECT_ALL;
            const areAllSelected =
              selectedGroupNames.length === visibleGroupNames.length ||
              selectedGroupNames.includes(SELECT_ALL);
            return (
              <StyledOption
                key={option}
                className={`option${
                  selectedGroupNames.includes(option) ? " selected" : ""
                }${open === "preview" ? " preview" : ""}${isSelectAll ? " selectAll" : ""}`}
                {...props}
                onClick={() => {
                  if (isSelectAll) {
                    return;
                  }
                  setGroupTilesFilterState(prev => ({
                    ...prev,
                    selectedGroupNames: prev.selectedGroupNames.includes(option)
                      ? prev.selectedGroupNames.filter(name => name !== option)
                      : [...prev.selectedGroupNames, option],
                  }));
                }}
              >
                <Checkbox
                  checked={
                    areAllSelected || selectedGroupNames.includes(option)
                  }
                />
                <div>{isSelectAll ? "Select All" : option}</div>
              </StyledOption>
            );
          },
          groupBy: option => {
            const associatedGroup = visibleGroups.find(
              group => group.longName === option || group.shortName === option
            );
            const associatedWorkSchedule =
              associatedGroup?.processes[0]?.workScheduleConfig?.id;
            const workScheduleName = workSchedules.find(
              ws => ws.id === associatedWorkSchedule
            )?.name;
            return option === SELECT_ALL
              ? "Select All"
              : `${associatedWorkSchedule} - ${workScheduleName}`;
          },
          renderInput: params => (
            <TextField
              {...params}
              placeholder={searchTextDisplay || translate("Search for a group")}
              value={searchTextDisplay}
              InputProps={{
                key: params.InputProps.key,
                ...omit(params.InputProps, "key"),
                ...{
                  startAdornment: (
                    <div style={{ display: "flex" }}>
                      <Search
                        style={{
                          color: lightTextBody,
                        }}
                      />
                      <SelectedWorkschedulesChips />
                    </div>
                  ),
                  endAdornment:
                    searchTextDisplay || selectedGroupNames.length > 0 ? (
                      <IconButton
                        className="btnClearInput"
                        onClick={() => {
                          // if there are too many groups and the page becomes slow when rendering/filtering, we can use startTransition commented-out lines below to ameliorate
                          // startProcessTilesPanelPending();
                          // setFilterState(prev => ({
                          //   ...prev,
                          //   searchTextDisplay: "",
                          // }));
                          // startTransition(() => {
                          setGroupTilesFilterState(p => ({
                            ...p,
                            searchTextDisplay: "",
                            searchText: null,
                            selectedGroupNames: [],
                          }));
                          // });
                        }}
                      >
                        <Close />
                      </IconButton>
                    ) : null,
                },
                onChange: e => {
                  // update the display value immediately
                  setGroupTilesFilterState(prev => ({
                    ...prev,
                    searchTextDisplay: e.target.value,
                  }));
                  // if there are too many groups and the page becomes slow when rendering/filtering, we can use startTransition commented-out lines below to ameliorate
                  // startProcessTilesPanelPending();
                  // update the search results when it won't freeze the app
                  // startTransition(() => {
                  //   stopProcessTilesPanelPending();

                  setGroupTilesFilterState(p => ({
                    ...p,
                    searchText: e.target.value,
                  }));
                  // });
                },
              }}
            />
          ),
          filterOptions: (options: string[], { inputValue, ...rest }) => {
            // keep ones that include all input words
            const filteredOptions = options.filter(option =>
              Boolean(
                inputValue.split(" ").reduce((isMatchAcc, word) => {
                  // make sure each word in the input is included in the option
                  return Boolean(
                    isMatchAcc &&
                      option.toLowerCase().includes(word.toLowerCase())
                  );
                }, true)
              )
            );
            return filteredOptions;
          },
        }}
      />
    </GroupTilesFilterControlsStyles>
  );
}

function SelectedWorkschedulesChips() {
  const [{ selectedGroupNames, searchTextDisplay }, setGroupTilesFilterState] =
    useGroupTilesFilterState();
  return (
    <ChipsStyles>
      {selectedGroupNames.map((name, index) => (
        <Chip
          key={index}
          label={name}
          onDelete={() => {
            setGroupTilesFilterState(prev => ({
              ...prev,
              selectedGroupNames: prev.selectedGroupNames.filter(
                n => n !== name
              ),
            }));
          }}
        />
      ))}
      {searchTextDisplay ? (
        <Chip
          label={
            <div style={{ display: "flex", gap: 4 }}>
              <Search style={{ opacity: 0.6 }} />
              <div>
                Name contains: {`"`}
                {searchTextDisplay}
                {`"`}
              </div>
            </div>
          }
          onDelete={() => {
            setGroupTilesFilterState(prev => ({
              ...prev,
              searchTextDisplay: "",
              searchText: null,
            }));
          }}
        />
      ) : null}
    </ChipsStyles>
  );
}
const ChipsStyles = styled.div`
  margin-left: 0.5em;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
  .MuiChip-root {
    height: 28px;
  }
`;

const GroupTilesFilterControlsStyles = styled.div``;

/** prevent listbox scroll position jumping back to top after clicking an option */
const CustomListbox = forwardRef((props: any, ref: any) => {
  const { children, ...other } = props;

  useEffect(() => {
    // get data-scroll-position on element [class*=GroupTilesFilterControlsStyles]
    const scrollPosition = document
      .querySelector("[class*=GroupTilesFilterControlsStyles]")
      ?.getAttribute("data-scroll-position");
    if (scrollPosition) {
      // set scroll position on element .custom-listbox
      document
        .querySelector(".MuiAutocomplete-listbox")
        ?.scrollTo(0, Number(scrollPosition));
    }
  }, [props]);

  const handleScroll = event => {
    // set data-scroll-position on element [class*=GroupTilesFilterControlsStyles]
    document
      .querySelector("[class*=GroupTilesFilterControlsStyles]")
      ?.setAttribute(
        "data-scroll-position",
        event.currentTarget.scrollTop?.toString()
      );
  };

  return (
    <ul
      className="custom-listbox"
      {...other}
      ref={ref}
      onScroll={handleScroll}
      style={{
        maxHeight: "calc(100vh - 360px)",
        overflowY: "auto",
      }}
    >
      {children}
    </ul>
  );
});
